import { StateCreator } from 'zustand';
import { api } from '../../api/client';

export interface Pipeline {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  stages: Stage[];
  created_at?: string;
  updated_at?: string;
}

export interface Stage {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  probability?: number;
  order?: number;
}

export interface PipelinesSlice {
  pipelines: Pipeline[];
  currentPipeline: Pipeline | null;
  pipelinesLoading: boolean;
  pipelinesError: string | null;

  fetchPipelines: () => Promise<void>;
  fetchPipeline: (id: string) => Promise<void>;
  createPipeline: (data: Partial<Pipeline>) => Promise<Pipeline>;
  updatePipeline: (id: string, data: Partial<Pipeline>) => Promise<void>;
  deletePipeline: (id: string) => Promise<void>;

  addStage: (pipelineId: string, stage: Partial<Stage>) => Promise<void>;
  removeStage: (pipelineId: string, stageId: string) => Promise<void>;

  handlePipelineSSE: (event: any) => void;

  setCurrentPipeline: (pipeline: Pipeline | null) => void;
}

export const createPipelinesSlice: StateCreator<PipelinesSlice> = (set, get) => ({
  pipelines: [],
  currentPipeline: null,
  pipelinesLoading: false,
  pipelinesError: null,

  fetchPipelines: async () => {
    set({ pipelinesLoading: true, pipelinesError: null });

    try {
      const response = await api.get('/pipelines');
      set({
        pipelines: response.data || [],
        pipelinesLoading: false,
      });
    } catch (error: any) {
      set({
        pipelinesError: error.response?.data?.message || 'Ошибка загрузки воронок',
        pipelinesLoading: false,
      });
    }
  },

  fetchPipeline: async (id: string) => {
    set({ pipelinesLoading: true, pipelinesError: null });

    try {
      const response = await api.get(`/pipelines/${id}`);
      set({
        currentPipeline: response.data,
        pipelinesLoading: false,
      });
    } catch (error: any) {
      set({
        pipelinesError: error.response?.data?.message || 'Ошибка загрузки воронки',
        pipelinesLoading: false,
      });
    }
  },

  createPipeline: async (data: Partial<Pipeline>) => {
    try {
      const response = await api.post('/pipelines', data);
      const newPipeline = response.data;

      set((state) => ({
        pipelines: [...state.pipelines, newPipeline],
      }));

      return newPipeline;
    } catch (error: any) {
      throw error;
    }
  },

  updatePipeline: async (id: string, data: Partial<Pipeline>) => {
    try {
      const response = await api.patch(`/pipelines/${id}`, data);
      const updated = response.data;

      set((state) => ({
        pipelines: state.pipelines.map((p) =>
          p.id === id || p._id === id ? updated : p
        ),
        currentPipeline:
          state.currentPipeline?.id === id || state.currentPipeline?._id === id
            ? updated
            : state.currentPipeline,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  deletePipeline: async (id: string) => {
    try {
      await api.delete(`/pipelines/${id}`);

      set((state) => ({
        pipelines: state.pipelines.filter((p) => p.id !== id && p._id !== id),
        currentPipeline:
          state.currentPipeline?.id === id || state.currentPipeline?._id === id
            ? null
            : state.currentPipeline,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  addStage: async (pipelineId: string, stage: Partial<Stage>) => {
    try {
      await api.post(`/pipelines/${pipelineId}/stages`, stage);
      // Перезагружаем воронку
      await get().fetchPipeline(pipelineId);
    } catch (error: any) {
      throw error;
    }
  },

  removeStage: async (pipelineId: string, stageId: string) => {
    try {
      await api.delete(`/pipelines/${pipelineId}/stages/${stageId}`);
      // Перезагружаем воронку
      await get().fetchPipeline(pipelineId);
    } catch (error: any) {
      throw error;
    }
  },

  handlePipelineSSE: (event) => {
    const { type, data } = event;

    switch (type) {
      case 'pipeline.stage_added':
        // Перезагружаем воронку
        if (get().currentPipeline?.id === data.pipelineId) {
          get().fetchPipeline(data.pipelineId);
        }
        break;

      case 'pipeline.stage_removed':
        // Перезагружаем воронку
        if (get().currentPipeline?.id === data.pipelineId) {
          get().fetchPipeline(data.pipelineId);
        }
        break;
    }
  },

  setCurrentPipeline: (pipeline: Pipeline | null) => {
    set({ currentPipeline: pipeline });
  },
});
